import config from '../../../config/config';
import getRequest from '../base/get';
import patchRequest from '../base/patch';
import postRequest from '../base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Fetch the Accounts list with pagination
   * @param {any} query query
   * @returns {any} promise
   */
  fetchConsumers: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/users`, {
      ...parameters,
    });
  },
  /**
   * Fetch the Accounts list with pagination
   * @param {any} query query
   * @returns {any} promise
   */
  fetchConsumersExport: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/users-export`, {
      ...parameters,
    });
  },
  /**
   * Fetching personal chargers details
   * @param {any} id - The ID of the personal charger
   * @param {any} query - Optional query parameters for the request
   * @returns {any} A promise resolving to the charger details
   */
  personalChargerDetails: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.emsp.DriversApiUrl}api/v1/charging-point/personal-charging-points/${id}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the Basic Information
   * @returns {any} promise
   */
  fetchEndConsumerStatus: (): Promise<any> => {
    return getRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/status`);
  },
  /**
   * Fetch the Basic Information
   * @param {any} userCustomId user custom id
   * @returns {any} promise
   */
  fetchOtps: (userCustomId: string): Promise<any> => {
    return getRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/get-otps/${userCustomId}`);
  },
  /**
   * Fetch basic profile
   * @param {any} userCustomId user custom id
   * @returns {any} promise
   */
  fetchBasicProfile: (userCustomId: string): Promise<any> => {
    return getRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/${userCustomId}`, {});
  },

  /**
   * Edit driver
   * @param {any} userCustomId - The ID of the driver
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the edit response
   */
  editDriver: (userCustomId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return patchRequest (
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/${userCustomId}`,{
        ...parameters
      }
      
    );
  },
  /**
   * top up money to an end consumer's wallet
   * @param {any} endConsumerId - The end consumer ID
   * @param {any} query - The top up query parameters
   * @returns {any} A promise that resolves with the wallet top up response
   */
  topUpWallet: (endConsumerId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return postRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/top-up/${endConsumerId}`, {
      ...parameters,
    });
  },
  /**
   * Deduct money from an end consumer's wallet
   * @param {any} endConsumerId - The end consumer ID
   * @param {any} query - The deduct query parameters
   * @returns {any} A promise that resolves with the wallet deduction response
   */
  deductWallet: (endConsumerId: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return postRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/reduce-balance/${endConsumerId}`, {
      ...parameters,
    });
  },
  /**
   * Approved password reset function
   * @param {any} id - The facility user ID
   * @param {any} data - The payload (e.g., { newPassword: 'yourPassword' })
   * @returns {any} Promise<any>
   */
  resetPassword: (id: string, data: any): Promise<any> => {
    return patchRequest(
      `${config.emsp.DriversApiUrl}api/emsp/v1/drivers/reset-password/${id}`,
      data,
    );
  },
  /**
   * Syncs OCPI tokens for drivers.
   * @returns {any} A promise that resolves with the sync response.
   */
  syncOcpiTokens: (): Promise<any> => {
    return postRequest(`${config.emsp.DriversApiUrl}api/emsp/v1/drivers/sync-ocpi-tokens`, {});
  },
  /**
   * Fetch activity logs for a driver
   * @param {string} id - The driver or end consumer custom ID
   * @param {QueryParameters} [query] - Optional query parameters
   * @returns {Promise<any>} A promise resolving to the driver activity logs
   */
  fetchActivityLogs: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.notificationApiUrl}api/super-admin/v2/activity-log/drivers/${id}`,
      {
        ...parameters,
      },
    );
  },
};
